// components/ConsultationForm.jsx (Updated with Discount)
import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Phone,
  Mail,
  Globe,
  MessageSquare,
  Search,
  Tag,
} from "lucide-react";
import BASE_URL from "../../../../config/config";
import ConsultationTracking from "./ConsultationTracking";

// 💰 تنظیمات قیمت و محتوا
const CONSULTATION_CONFIG = {
  price: 5000000, // قیمت اصلی به تومان
  discountPercent: 0, // درصد تخفیف (0 = بدون تخفیف)
  // یا می‌تونید مستقیم قیمت نهایی رو بذارید:
  // discountedPrice: 4000000, // قیمت با تخفیف (اگر این رو بذارید، discountPercent نادیده گرفته میشه)
  duration: 60, // مدت جلسه به دقیقه
  availableSlots: 3, // تعداد جای خالی این هفته
};

const ConsultationForm = () => {
  const [activeTab, setActiveTab] = useState("form");
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    websiteUrl: "",
    mainProblem: "",
    source: "direct",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCsrfToken(data.csrfToken))
      .catch((err) => console.error("خطا در دریافت CSRF:", err));
  }, []);

  // 🧮 محاسبه قیمت نهایی
  const calculateFinalPrice = () => {
    const { price, discountPercent, discountedPrice } = CONSULTATION_CONFIG;

    // اگر discountedPrice تعیین شده باشه، از اون استفاده کن
    if (discountedPrice && discountedPrice > 0) {
      return {
        originalPrice: price,
        finalPrice: discountedPrice,
        hasDiscount: true,
        discountAmount: price - discountedPrice,
        discountPercent: Math.round(((price - discountedPrice) / price) * 100),
      };
    }

    // اگر فقط درصد تخفیف تعیین شده باشه
    if (discountPercent && discountPercent > 0) {
      const finalPrice = price - (price * discountPercent) / 100;
      return {
        originalPrice: price,
        finalPrice: Math.round(finalPrice),
        hasDiscount: true,
        discountAmount: price - finalPrice,
        discountPercent: discountPercent,
      };
    }

    // بدون تخفیف
    return {
      originalPrice: price,
      finalPrice: price,
      hasDiscount: false,
      discountAmount: 0,
      discountPercent: 0,
    };
  };

  const priceInfo = calculateFinalPrice();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/consultation/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          amount: priceInfo.finalPrice, // 🎯 قیمت نهایی (با یا بدون تخفیف)
          originalAmount: priceInfo.originalPrice, // 🎯 قیمت اصلی (برای لاگ)
          hasDiscount: priceInfo.hasDiscount, // 🎯 آیا تخفیف داشته؟
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "خطا در ثبت درخواست");
      }

      const paymentResponse = await fetch(
        `${BASE_URL}/consultation/payment/${data.data.orderId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
        }
      );

      const paymentData = await paymentResponse.json();

      if (!paymentResponse.ok) {
        throw new Error(paymentData.message || "خطا در اتصال به درگاه");
      }

      window.location.href = paymentData.data.paymentUrl;
    } catch (err) {
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // 🔢 فرمت کردن قیمت
  const formatPrice = (price) => {
    return new Intl.NumberFormat("fa-IR").format(price);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 font-sans"
      dir="rtl"
    >
      <div className="max-w-6xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* بخش چپ: محتوای فروشی */}
          <div className="bg-gradient-to-br from-slate-500 via-blue-700 to-slate-900 text-white p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500 rounded-full opacity-10 -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-500 rounded-full opacity-10 -ml-20 -mb-20"></div>

            <div className="relative z-10">
              <div className="inline-block bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🔥 پیشنهاد ویژه
              </div>

              <h1 className="text-4xl font-bold mb-4 leading-tight">
                کالبدشکافیِ مسیرِ فروش
              </h1>

              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                سایت دارید اما فروش ندارید؟
                <br />
                در یک جلسه {CONSULTATION_CONFIG.duration} دقیقه‌ای، پتانسیل
                کسب‌وکارتان را کشف کنید.
              </p>

              <div className="bg-slate-800/50 rounded-xl p-6 mb-8 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="text-green-400" size={20} />
                  <span className="font-semibold">
                    تحلیل جامع UX/UI و روانشناسی فروش
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="text-green-400" size={20} />
                  <span className="font-semibold">
                    شناسایی نقاط اصطکاک در مسیر خرید
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-400" size={20} />
                  <span className="font-semibold">
                    نقشه راه عملیاتی برای بهبود فروش
                  </span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-400">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">تحلیل رفتار کاربر</h3>
                    <p className="text-sm text-slate-400">
                      بررسی مسیر کاربر و کشف موانع پنهان خرید
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-400">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">تحلیل فنی و محتوایی</h3>
                    <p className="text-sm text-slate-400">
                      بررسی UX/UI + کپی‌رایتینگ و اصول متقاعدسازی
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-400">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">برنامه اقدام عملیاتی</h3>
                    <p className="text-sm text-slate-400">
                      اولویت‌بندی تغییرات برای حداکثر تأثیر
                    </p>
                  </div>
                </div>
              </div>

              {/* نمایش قیمت با تخفیف */}
              <div className="border-t border-slate-700 pt-6">
                <p className="text-sm text-slate-400 mb-2">سرمایه‌گذاری شما:</p>

                {priceInfo.hasDiscount ? (
                  <div className="space-y-2">
                    {/* قیمت اصلی (خط خورده) */}
                    <div className="flex items-center gap-3">
                      <p className="text-2xl font-bold text-slate-500 line-through decoration-red-500 decoration-2">
                        {formatPrice(priceInfo.originalPrice)}
                      </p>
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Tag size={12} />
                        {priceInfo.discountPercent}% تخفیف
                      </span>
                    </div>

                    {/* قیمت نهایی */}
                    <div className="flex items-baseline gap-3">
                      <p className="text-4xl font-bold text-green-400">
                        {formatPrice(priceInfo.finalPrice)}
                      </p>
                      <p className="text-slate-400">تومان</p>
                    </div>

                    {/* مبلغ صرفه‌جویی */}
                    <p className="text-sm text-green-400">
                      🎉 شما {formatPrice(priceInfo.discountAmount)} تومان
                      صرفه‌جویی می‌کنید!
                    </p>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <p className="text-4xl font-bold text-white">
                      {formatPrice(priceInfo.finalPrice)}
                    </p>
                    <p className="text-slate-400">تومان</p>
                  </div>
                )}

                <p className="text-xs text-amber-400 mt-2">
                  ⚠️ فقط {CONSULTATION_CONFIG.availableSlots} جای خالی این هفته
                </p>
              </div>
            </div>
          </div>

          {/* بخش راست: تب‌ها */}
          <div className="bg-white">
            {/* Tab Headers */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setActiveTab("form")}
                className={`flex-1 py-4 text-center font-semibold transition-all ${
                  activeTab === "form"
                    ? "text-slate-800 border-b-2 border-slate-600 bg-slate-50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                رزرو جلسه
              </button>
              <button
                onClick={() => setActiveTab("tracking")}
                className={`flex-1 py-4 text-center font-semibold transition-all flex items-center justify-center gap-2 ${
                  activeTab === "tracking"
                    ? "text-slate-800 border-b-2 border-slate-600 bg-slate-50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Search size={18} />
                پیگیری سفارش
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-12">
              {/* فرم رزرو */}
              {activeTab === "form" && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    رزرو جلسه مشاوره
                  </h2>
                  <p className="text-slate-600 mb-8">
                    فرم زیر را تکمیل کنید تا برای هماهنگی با شما تماس بگیریم
                  </p>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <AlertCircle
                        className="text-red-500 flex-shrink-0 mt-0.5"
                        size={20}
                      />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* نام */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        نام و نام خانوادگی{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition outline-none"
                        placeholder="علی محمدی"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </div>

                    {/* شماره تماس */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        شماره تماس <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                          size={18}
                        />
                        <input
                          type="tel"
                          name="phone"
                          required
                          pattern="09[0-9]{9}"
                          className="w-full pr-12 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition outline-none"
                          placeholder="09123456789"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* ایمیل */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        ایمیل (اختیاری)
                      </label>
                      <div className="relative">
                        <Mail
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                          size={18}
                        />
                        <input
                          type="email"
                          name="email"
                          className="w-full pr-12 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition outline-none"
                          placeholder="example@domain.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* آدرس وبسایت */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        آدرس وبسایت (اختیاری)
                      </label>
                      <div className="relative">
                        <Globe
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                          size={18}
                        />
                        <input
                          type="url"
                          name="websiteUrl"
                          className="w-full pr-12 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition outline-none"
                          placeholder="https://example.com"
                          value={formData.websiteUrl}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* مشکل اصلی */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        بزرگ‌ترین مشکل شما چیست؟ (اختیاری)
                      </label>
                      <div className="relative">
                        <MessageSquare
                          className="absolute right-4 top-4 text-slate-400"
                          size={18}
                        />
                        <textarea
                          name="mainProblem"
                          rows="4"
                          className="w-full pr-12 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition outline-none resize-none"
                          placeholder="مثلاً: فروش پایین، ترافیک زیاد اما خرید صفر، و..."
                          value={formData.mainProblem}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* دکمه ارسال */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-l from-green-500 to-green-600 text-white font-bold py-4 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          <span>در حال پردازش...</span>
                        </>
                      ) : (
                        <span>
                          {priceInfo.hasDiscount ? (
                            <>
                              رزرو و پرداخت {formatPrice(priceInfo.finalPrice)}{" "}
                              تومان
                              <span className="text-green-200 text-sm mr-2">
                                (بجای {formatPrice(priceInfo.originalPrice)})
                              </span>
                            </>
                          ) : (
                            `رزرو و پرداخت ${formatPrice(
                              priceInfo.finalPrice
                            )} تومان`
                          )}
                        </span>
                      )}
                    </button>

                    {/* توضیحات امنیتی */}
                    <p className="text-xs text-slate-500 text-center mt-4">
                      🔒 پرداخت از طریق درگاه امن زرین‌پال
                    </p>
                  </form>
                </div>
              )}

              {/* بخش پیگیری */}
              {activeTab === "tracking" && (
                <div>
                  <ConsultationTracking embedded={true} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationForm;
